import ThirdPartyProcessor from "../../../model/ThirdPartyProcessors";
import AnnualReportForm from "../../../model/AnnualReportForm";
import {useState} from "react";
import {DOI_LINK} from "../../../AnnualReportConstants";

const THIRD_PARTY_PROCESSOR_QUESTION_TEXT = `Please provide the names of any Third-Party Processors* who process UK 
            Biobank data as a sub-contractor for this Research Project and provide details of the tasks the Third-Party 
            Processor conducts on your behalf.`;
const THIRD_PARTY_PROCESSOR_CLARIFICATION = `* The Third-Party Processor is not a Collaborator and shall only be engaged 
            for the purposes of discrete elements of data computation and analysis in relation to the Permitted Purpose 
            (the "Processor Task").`;
const REQUEST_DOI_LINK_FOR_THIRD_PARTY_PROCESSOR = (
   <>
      Please share the <a href={DOI_LINK}>DOI</a> links for any code Third Party Processors have used for any work
      using UK Biobank data (preferably hosted in an open-source repository like GitHub)
   </>
);

/**
 * Props for this component
 */
interface ThirdPartyProcessorQuestionProps {
   form: AnnualReportForm,
   isLocked: boolean,
}

/**
 * Common question for Annual Reports.
 *
 * Used in: {@link CollaboratorAnnualReportForm|Collaborator annual report}, {@link ApplicantAnnualReportForm|Applicant annual report}
 */
export default function ThirdPartyProcessorQuestion({form, isLocked}: ThirdPartyProcessorQuestionProps) {
   // Populate the state with either the list of saved processors from the form or an empty list
   const [thirdPartyProcessors, setThirdPartyProcessors] = useState<ThirdPartyProcessor[]>(splitProcessors(form.thirdPartyProcessors, form.locked));

   /**
    * When a row is updated, it will store the new value in its respective element. If the element being updated
    * was the empty one (The array will always have one), it will create a new element at the end of the array.
    *
    * @param index The index of the Third Party Processor in the array
    * @param field Which field is being edited (Name, Task or Checkbox)
    * @param value The new value of the field
    */
   const updateProcessor = (index: number, field: 'name' | 'task' | 'doi' | 'check', value: string | boolean): void => {
      // Update entry
      const temp: ThirdPartyProcessor[] = [...thirdPartyProcessors];
      if (field === 'check') {
         temp[index].isStillProcessingData = value as boolean;
      } else {
         temp[index][field] = value as string;
      }

      // If it's the last processor and user starts typing, add a new one
      if (index === temp.length - 1 && value !== '') {
         temp.push({name: '', task: '', doi: '', isStillProcessingData: false});
      }

      form.thirdPartyProcessors = mergeProcessors(temp);
      setThirdPartyProcessors(temp);
   }

   /**
    * Remove a processors from the array
    *
    * @param index The index of the element we wish to delete
    */
   const removeProcessor = (index: number): void => {
      const temp: ThirdPartyProcessor[] = [...thirdPartyProcessors];
      temp.splice(index, 1);

      form.thirdPartyProcessors = mergeProcessors(temp);
      setThirdPartyProcessors(temp);
   }

   return (
      <div id="3PPQ" className="annual-report-question">
         <label id="3PPQMainText" className="question-text">{THIRD_PARTY_PROCESSOR_QUESTION_TEXT}</label>
         <label id="3PPQExtraInfo1" className="extra-info">{THIRD_PARTY_PROCESSOR_CLARIFICATION}</label>
         <label id="3PPQExtraInfo2" className="extra-info">{REQUEST_DOI_LINK_FOR_THIRD_PARTY_PROCESSOR}</label>

         {/*Third Party processors' entries*/}
         {thirdPartyProcessors.map((processor, index) => (
            <div id={`3PPQProcessor_${index}`} key={index}
                 className="multi-entry-question">
               <div id={`3PPQProcessorEntryArea_${index}`}>
                  <input
                     id={`3PPQProcessorName_${index}`}
                     type="text"
                     className="form-control"
                     placeholder="Processor"
                     value={processor.name}
                     disabled={isLocked}
                     onChange={(e) => updateProcessor(index, 'name', e.target.value)}
                     required={(index !== thirdPartyProcessors.length - 1 && !isLocked)}
                  />
                  <input
                     id={`3PPQProcessorTask_${index}`}
                     type="text"
                     className="form-control"
                     placeholder="Tasks"
                     value={processor.task}
                     disabled={isLocked}
                     onChange={(e) => updateProcessor(index, 'task', e.target.value)}
                     required={(index !== thirdPartyProcessors.length - 1 && !isLocked)}
                  />
                  <input
                     id={`3PPQProcessorDOI_${index}`}
                     type="text"
                     className="form-control"
                     placeholder="DOI link"
                     value={processor.doi}
                     disabled={isLocked}
                     onChange={(e) => updateProcessor(index, 'doi', e.target.value)}
                     required={(index !== thirdPartyProcessors.length - 1 && !isLocked)}
                  />
                  <div id={`3PPQProcessorCheckDiv_${index}`} className="third-party-check">
                     <label className="form-check-label" htmlFor={`3PPQProcessorCheck_${index}`}>Is still
                        processing UK Biobank data?</label>
                     {(isLocked) ?
                        <span id={`3PPQProcessorCheck_${index}`}
                              className="boolean-answer">{processor.isStillProcessingData ? 'YES' : 'NO'}</span>
                        :
                        <input type="checkbox" className="form-check-input" id={`3PPQProcessorCheck_${index}`}
                               checked={processor.isStillProcessingData}
                               onChange={() => updateProcessor(index, 'check', !processor.isStillProcessingData)}/>
                     }
                  </div>
               </div>
               {/*Show delete button if this is not the empty element*/}
               {(index !== thirdPartyProcessors.length - 1 && !isLocked) ?
                  (<button id={`3PPQProcessorRemove_${index}`}
                           type="button"
                           className="btn-close"
                           aria-label="Close"
                           onClick={() => removeProcessor(index)}></button>
                  ) : null
               }
            </div>
         ))}
      </div>
   );
}

/**
 * This method will gather the data from the third party processors and merge it into a single string
 *
 * @param processors The array of processors
 */
function mergeProcessors(processors: ThirdPartyProcessor[]): string {
   let stringWithProcessors: string = "";

   for (let i = 0; i < processors.length - 1; i++) {
      // Don't include the ';' at the beginning
      if (i === 0) {
         stringWithProcessors = `${encodeURIComponent(processors[i].name)}|${encodeURIComponent(processors[i].task)}|${encodeURIComponent(processors[i].doi)}|${(processors[i].isStillProcessingData ? 1 : 0)}`;
         continue;
      }
      stringWithProcessors = `${stringWithProcessors};${encodeURIComponent(processors[i].name)}|${encodeURIComponent(processors[i].task)}|${encodeURIComponent(processors[i].doi)}|${(processors[i].isStillProcessingData ? 1 : 0)}`;
   }

   return stringWithProcessors;
}

/**
 * This function will separate the third party processors associated with this report
 *
 * @param formString The processors on the form (Can be null)
 * @param isLocked A boolean to show if the report is blocked or not so we can show the empty row
 */
function splitProcessors(formString: string | null, isLocked: boolean): ThirdPartyProcessor[] {
   const arrayOfProcessors: ThirdPartyProcessor[] = [];
   if (formString) {
      // Split into individual processors
      const splitString: string[] = formString.split(';');

      splitString.forEach((line: string) => {
         // Split into individual parts
         const parts: string[] = line.split('|');
         arrayOfProcessors.push({
            name: decodeURIComponent(parts[0]),
            task: decodeURIComponent(parts[1]),
            doi: decodeURIComponent(parts[2]),
            isStillProcessingData: parts[3] === '1'
         });
      })
   }

   if (!isLocked) {
      arrayOfProcessors.push({name: '', task: '', doi: '', isStillProcessingData: false})
   }

   return arrayOfProcessors;
}
